import type React from 'react'
import { ROTATE_CURSOR } from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import { CROP_ACCENT_COLOR } from '../constants/crop-chrome'
import type { RotateDialLayout } from '../helpers/badge-layout'

const ROTATE_ICON_PATH =
  'M14 8C14 9.18669 13.6481 10.3467 12.9888 11.3334C12.3295 12.3201 11.3925 13.0892 10.2961 13.5433C9.19975 13.9974 7.99335 14.1162 6.82946 13.8847C5.66558 13.6532 4.59648 13.0818 3.75736 12.2426C2.91825 11.4035 2.3468 10.3344 2.11529 9.17054C1.88378 8.00666 2.0026 6.80026 2.45673 5.7039C2.91085 4.60754 3.67989 3.67047 4.66658 3.01118C5.65328 2.35189 6.81331 2 8 2C9.68 2 11.2867 2.66667 12.4933 3.82667L14 5.33333M10.6667 5.33333H14L14 2'

interface CropRotateDialProps {
  layout: RotateDialLayout
  /** Stroke width (canvas units). */
  sw: number
  isRotating: boolean
  isPanning: boolean
  onRotateDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: (e: React.PointerEvent) => void
}

/** The photo-rotate dial (see `computeRotateDialLayout`): the rotate badge plus its stem, dot and
 *  icon glyph. */
export function CropRotateDial({
  layout,
  sw,
  isRotating,
  isPanning,
  onRotateDown,
  onPointerMove,
  onPointerUp,
}: CropRotateDialProps) {
  return (
    <>
      {/* Rotate badge hit-target — must stay mounted for the entire ROTATE drag (it's the element
        that calls setPointerCapture in handleRotateDown; unmounting it mid-gesture would
        release capture and strand the drag) — but its visual appearance hides during a PAN
        drag too (the move badge, this badge, and the connecting line/stem are one visual unit
        that should disappear together while repositioning the photo), using the same
        transparent-fill-still-hit-testable technique the interior pan rect uses. Rendered
        BEFORE the icon glyph below (not after) so the icon paints on top of the badge's fill,
        matching the move badge's own stacking — drawing this circle's semi-transparent white
        fill on top of the glyph (the previous order) washed the icon's color out, making it
        look faded/under-a-layer next to the crisp move icon. */}
      <circle
        data-testid="crop-rotate-badge"
        cx={layout.rotateCenterX}
        cy={layout.rotateCenterY}
        r={layout.rotateR}
        fill={isRotating || isPanning ? 'transparent' : 'rgba(255, 255, 255, 0.6)'}
        stroke={isRotating || isPanning ? 'transparent' : CROP_ACCENT_COLOR}
        strokeWidth={sw}
        vectorEffect="non-scaling-stroke"
        style={{
          pointerEvents: 'all',
          cursor: ROTATE_CURSOR,
          filter:
            isRotating || isPanning ? undefined : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.05))',
        }}
        onPointerDown={onRotateDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />

      {/* Rotate handle stem + decorative accessories — connecting line/dot/icon glyph, positioned
        like a dial pointer around the shape's center (see the geometry comment on
        `computeRotateDialLayout`). Safe to fully unmount during a rotate OR pan drag: none of
        these hold pointer capture. */}
      {!isRotating && !isPanning && (
        <>
          <circle
            data-testid="crop-rotate-stem-dot"
            cx={layout.stemStartX}
            cy={layout.stemStartY}
            r={layout.stemDotR}
            fill={CROP_ACCENT_COLOR}
            pointerEvents="none"
          />
          <line
            data-testid="crop-rotate-line"
            x1={layout.stemStartX}
            y1={layout.stemStartY}
            x2={layout.lineEndX}
            y2={layout.lineEndY}
            stroke={CROP_ACCENT_COLOR}
            strokeWidth={sw}
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
          <g transform={layout.rotateIconTransform} pointerEvents="none">
            <path
              d={ROTATE_ICON_PATH}
              stroke={CROP_ACCENT_COLOR}
              strokeWidth={1.33}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        </>
      )}
    </>
  )
}
